/**
 * Top-level navigation context. The whole app shares a single stack so we can
 * drill rule -> card -> rule -> keyword -> rulebook section with a Back
 * button. The active tab is computed from the head of the stack.
 */
import React, { createContext, useContext, useCallback, useState } from "react";

const NavContext = createContext(null);

export const ROOT_VIEWS = {
  rules: { type: "rules-root" },
  rulebook: { type: "rulebook-root" },
  steamroller: { type: "steamroller-root" },
  propose: { type: "propose" },
  history: { type: "history" },
  admin: { type: "admin-root" },
};

function tabOf(view) {
  if (!view) return "rules";
  if (view.type === "propose") return "propose";
  if (view.type === "history" || view.type === "history-detail" || view.type === "model-check-detail") return "history";
  if (view.type.startsWith("steamroller")) return "steamroller";
  if (view.type.startsWith("rulebook")) return "rulebook";
  if (view.type.startsWith("admin")) return "admin";
  return "rules"; // rules-root, rule, card
}

export function NavProvider({ children }) {
  const [stack, setStack] = useState([ROOT_VIEWS.rules]);
  const top = stack[stack.length - 1];
  const tab = tabOf(top);

  const navigate = useCallback((view) => {
    setStack((s) => [...s, view]);
  }, []);
  const replace = useCallback((view) => {
    setStack((s) => [...s.slice(0, -1), view]);
  }, []);
  const back = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);
  const switchTab = useCallback((t) => {
    setStack([ROOT_VIEWS[t] || ROOT_VIEWS.rules]);
  }, []);
  const goHome = useCallback(() => {
    setStack([ROOT_VIEWS.rules]);
  }, []);

  const value = {
    view: top,
    canGoBack: stack.length > 1,
    tab,
    navigate,
    replace,
    back,
    switchTab,
    goHome,
  };
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav() {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav must be inside <NavProvider>");
  return ctx;
}

export const VIEWS = {
  rule: (id) => ({ type: "rule", id }),
  card: (id) => ({ type: "card", id }),
  rulebookSection: (id) => ({ type: "rulebook-section", id }),
  steamrollerSection: (id) => ({ type: "steamroller-section", id }),
  history: () => ({ type: "history" }),
  spellOverlap: () => ({ type: "spell-overlap" }),
  historyDetail: (id) => ({ type: "history-detail", id }),
  modelCheckDetail: (id) => ({ type: "model-check-detail", id }),
};
